import { useMemo, useState, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

import { plural, t, Trans } from '@lingui/macro';

import ChevronLeft from '@decisiv/iconix/lib/components/ChevronLeft';
import Plus from '@decisiv/iconix/lib/components/Plus';

import { H4, P } from '@decisiv/ui-components/lib/components/Typography';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import { useWizardModal } from 'components/WizardModal';

import useTeamIdValidQuery from 'pages/Teams/hooks/useTeamIdValidQuery';
import { FLEET, PLATFORM_ACCOUNT_TYPES } from 'utils/SentinelTeam/constants';

import useDebounce from 'utils/useDebounce';
import {
  generateSourceValidationError,
  generateApiUserText,
} from 'utils/SentinelTeam';

import useUpdateEffect from 'hooks/useUpdateEffect';

import {
  UsernameContainer,
  SourceItemsContainer,
  DisplaySourceIdsContainer,
} from './styles';
import ManagementSourceItem from '../SourceItem';
import { useManagementTeamModalContext } from '../contexts/ManagementTeamModalContext';

export default function ManagementTeamSettingsStep() {
  const { handlePrevPage } = useWizardModal();
  const {
    values,
    setValues,
    invalidEmailSources,
    sourceIdsWithErrors,
    sourceIdError,
    setSourceIdError,
  } = useManagementTeamModalContext();

  const { sourceIds } = values;

  const [username, setUsername] = useState(values.username);
  const [password, setPassword] = useState(values.password);
  const [ownerEmail, setOwnerEmail] = useState(values.ownerEmail);
  const [sourceId, setSourceId] = useState('');

  const debouncedSourceId = useDebounce(sourceId, 500);
  const debouncedUsername = useDebounce(username, 500);
  const debouncedPassword = useDebounce(password, 500);
  const debouncedOwnerEmail = useDebounce(ownerEmail, 500);

  // teamType is always FLEET in this variant
  const teamType = FLEET;
  const sourceType = PLATFORM_ACCOUNT_TYPES.DEALER_ID;

  const apiInfoText = generateApiUserText(teamType);

  const accountsText = useMemo(
    () => t`${sourceIds.length} Fleet Accounts`,
    [sourceIds.length],
  );

  const passwordWarning = useMemo(() => {
    const accountText = plural(sourceIdsWithErrors.length, {
      one: 'account',
      other: 'accounts',
    });

    let sourceIdsText = '';

    /* istanbul ignore next -- @preserve */
    if (sourceIdsWithErrors.length > 1) {
      const commaIds = sourceIdsWithErrors
        .slice(0, sourceIdsWithErrors.length - 1)
        .map((src) => src.value);
      const suffixId = sourceIdsWithErrors
        .slice(sourceIdsWithErrors.length - 1, sourceIdsWithErrors.length)
        .map((src) => src.value);

      sourceIdsText = `${commaIds.join(', ')} and ${suffixId[0]}`;
    }
    /* istanbul ignore next -- @preserve */
    if (sourceIdsWithErrors.length === 1) {
      sourceIdsText = sourceIdsWithErrors[0].value;
    }

    return t`API user is not valid for fleet ${accountText} ${sourceIdsText}`;
  }, [sourceIdsWithErrors]);

  const canShowEmailWarning = useMemo(
    () =>
      invalidEmailSources.length > 0 &&
      /* istanbul ignore next -- @preserve */ !isEmpty(ownerEmail),
    [invalidEmailSources.length, ownerEmail],
  );

  const emailWarning = useMemo(
    () =>
      t`The Team Owner must already be a user within any of the fleet accounts added to this team. Enter their username or email as it appears in the Portal.`,
    [],
  );

  const handleSourceIdChange = useCallback(
    (e) => {
      e.persist();
      setSourceId(e.target.value);
      setSourceIdError([]);
    },
    [setSourceIdError],
  );

  const { data, loading: teamValidationLoading } = useTeamIdValidQuery({
    variables: {
      attributes: {
        teamType,
        platformAccount: {
          sourceId: debouncedSourceId,
          sourceType,
        },
      },
    },
    skip: isEmpty(debouncedSourceId),
    fetchPolicy: 'network-only',
    onCompleted: ({ platformAccountIdValid: { errors, valid } }) => {
      if (!valid) {
        setSourceIdError(errors);
      }
    },
  });

  const teamIdValid = data?.platformAccountIdValid?.valid ?? false;
  const dealerName = data?.platformAccountIdValid?.sourceName ?? '';

  const sourceIdAlreadyInList = useMemo(
    () => sourceIds.some((i) => i.value === sourceId),
    [sourceId, sourceIds],
  );

  const canAddSource = useMemo(
    () =>
      teamIdValid &&
      sourceIdError.length === 0 &&
      !sourceIds.map(({ value }) => value).includes(sourceId),
    [sourceIdError.length, teamIdValid, sourceIds, sourceId],
  );

  /* istanbul ignore next -- @preserve */
  const handleOnAddSourceId = useCallback(() => {
    if (!sourceId) return;
    if (sourceIds.map(({ value }) => value).includes(sourceId)) return;

    setValues((oldState) => ({
      ...oldState,
      sourceIds: [
        ...oldState.sourceIds,
        {
          dealerName,
          teamType,
          value: sourceId,
          sourceType,
          valid: false,
          validated: false,
          errors: [],
        },
      ],
    }));
    setSourceId('');
    setSourceIdError([]);
  }, [
    setValues,
    sourceId,
    setSourceIdError,
    dealerName,
    sourceType,
    sourceIds,
    teamType,
  ]);

  const handleUsernameChange = useCallback((e) => {
    e.persist();
    setUsername(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    e.persist();
    setPassword(e.target.value);
  }, []);

  const handleOwnerEmailChange = useCallback((e) => {
    e.persist();
    setOwnerEmail(e.target.value);
  }, []);

  /* istanbul ignore next -- @preserve */
  useUpdateEffect(() => {
    if (!sourceIdAlreadyInList) return;

    setSourceIdError((oldState) => ['SOURCE_IN_LIST', ...oldState]);
  }, [sourceIdAlreadyInList]);

  /* istanbul ignore next -- @preserve */
  useUpdateEffect(() => {
    setValues((oldState) => ({
      ...oldState,
      username: debouncedUsername,
      sourceIds: oldState.sourceIds.map((src) => ({
        ...src,
        validated: false,
        errors: [],
        emailValid: false,
      })),
    }));
  }, [debouncedUsername]);

  /* istanbul ignore next -- @preserve */
  useUpdateEffect(() => {
    setValues((oldState) => ({
      ...oldState,
      password: debouncedPassword,
      sourceIds: oldState.sourceIds.map((src) => ({
        ...src,
        validated: false,
        errors: [],
        emailValid: false,
      })),
    }));
  }, [debouncedPassword]);

  /* istanbul ignore next -- @preserve */
  useUpdateEffect(() => {
    setValues((oldState) => ({
      ...oldState,
      ownerEmail: debouncedOwnerEmail,
      sourceIds: oldState.sourceIds.map((src) => ({
        ...src,
        validated: false,
        errors: [],
        emailValid: false,
      })),
    }));
  }, [debouncedOwnerEmail]);

  return (
    <Flex flexDirection="column" marginTop={1}>
      <Flex flexDirection="column">
        <H4 marginTop={2}>
          <Trans>API User</Trans>
        </H4>
        <P color="alaskanHusky" size="small" marginBottom={1}>
          <Trans>
            An API User is an artificial user that makes API calls on behalf of
            the team. The API user must be a unique user created in the Portal
            and given Dealer Administrator permissions to {apiInfoText}. Enter
            their login information for the Portal.
          </Trans>
        </P>
        <UsernameContainer warning={sourceIdsWithErrors.length > 0}>
          <TextField
            required={sourceIds.length > 0}
            label={t`Username`}
            value={username}
            onChange={handleUsernameChange}
          />
        </UsernameContainer>
        <Flex marginTop={0.5}>
          <TextField
            required={sourceIds.length > 0}
            label={t`Password`}
            value={password}
            onChange={handlePasswordChange}
            type="password"
            warningMessage={
              /* istanbul ignore next -- @preserve */
              sourceIdsWithErrors.length > 0 ? passwordWarning : undefined
            }
          />
        </Flex>

        {/* Fleet IDs are optional; the helper text communicates this */}
        <H4 marginTop={1}>
          <Trans>Fleet IDs</Trans>
        </H4>
        <Flex alignItems="flex-start">
          <P size="small" shade={1}>
            <Trans>Enter the fleet account ID(s).</Trans>
          </P>
        </Flex>
        <Flex marginTop={1} alignItems="center">
          <TextField
            hideLabel
            aria-label={t`Fleet ID`}
            label={t`Fleet ID`}
            value={sourceId}
            onChange={handleSourceIdChange}
            helpMessage={t`Fleet IDs can be added later`}
            warningMessage={
              !teamValidationLoading
                ? generateSourceValidationError(
                    sourceIdError?.[0],
                    sourceType,
                    debouncedSourceId,
                  ) ?? undefined
                : undefined
            }
          />
          <Flex marginLeft={1} alignSelf="baseline">
            <Button
              icon={Plus}
              text={t`Add`}
              aria-label={t`Add Fleet ID`}
              kind="secondary"
              onClick={handleOnAddSourceId}
              disabled={!canAddSource}
            />
          </Flex>
        </Flex>
      </Flex>

      {sourceIds.length > 0 && (
        <DisplaySourceIdsContainer marginTop={1} flexDirection="column" display>
          <P color="alaskanHusky" size="small" marginBottom={1}>
            {accountsText}
          </P>
          <SourceItemsContainer
            flexDirection="column"
            data-testid="multi-source"
          >
            {sourceIds.map((source) => (
              <ManagementSourceItem key={source.value} source={source} />
            ))}
          </SourceItemsContainer>
        </DisplaySourceIdsContainer>
      )}

      {/* Team Owner */}
      <H4 marginTop={2}>
        <Trans>Team Owner</Trans>
      </H4>
      <P color="alaskanHusky" size="small" marginBottom={1}>
        <Trans>
          The Team Owner must already be a user within any of the fleet accounts
          added to this team. Enter their username or email as it appears in the
          Portal.
        </Trans>
      </P>
      <TextField
        required
        label={t`Username or Email`}
        onChange={handleOwnerEmailChange}
        value={ownerEmail}
        warningMessage={
          canShowEmailWarning
            ? /* istanbul ignore next -- @preserve */ emailWarning
            : undefined
        }
      />
      <Flex justifyContent="flex-start" marginTop={1.6}>
        <Button
          aria-label={t`Back`}
          text={t`Back`}
          variant="ghost"
          icon={ChevronLeft}
          iconPosition="left"
          onClick={handlePrevPage}
        />
      </Flex>
    </Flex>
  );
}
